import { useCallback } from "react";

/**
 * Custom hook: Tournament CRUD operations
 */
export const useTournamentCRUD = (tournaments, setTournaments, registros, setRegistros) => {
  const getTournament = useCallback(
    (id) => tournaments.find((t) => String(t.id) === String(id)),
    [tournaments]
  );

  const validateTournament = useCallback((form) => {
    const errors = {};
    if (!form.nome.trim()) errors.nome = "Nome obrigatório";
    if (!form.buyin || isNaN(form.buyin) || Number(form.buyin) <= 0)
      errors.buyin = "Buy-in inválido";
    if (!form.plataforma) errors.plataforma = "Selecione a plataforma";
    return errors;
  }, []);

  const saveTournament = useCallback(
    (form, editingId) => {
      const errors = validateTournament(form);
      if (Object.keys(errors).length) return { success: false, errors };

      setTournaments((prev) =>
        editingId
          ? prev.map((t) => (t.id === editingId ? { ...t, ...form } : t))
          : [
              {
                ...form,
                id: Date.now(),
                criadoEm: new Date().toISOString(),
              },
              ...prev,
            ]
      );

      return { success: true, errors: {} };
    },
    [validateTournament, setTournaments]
  );

  const deleteTournament = useCallback(
    (id) => {
      setTournaments((p) => p.filter((t) => t.id !== id));
      setRegistros((p) => p.filter((r) => r.torneioId !== id));
    },
    [setTournaments, setRegistros]
  );

  return {
    getTournament,
    validateTournament,
    saveTournament,
    deleteTournament,
  };
};

/**
 * Custom hook: Registro CRUD operations
 */
export const useRegistroCRUD = (tournaments, setRegistros) => {
  const getTournament = useCallback(
    (id) => tournaments.find((t) => String(t.id) === String(id)),
    [tournaments]
  );

  const validateRegistro = useCallback(
    (form) => {
      const errors = {};
      if (!form.torneioId) errors.torneioId = "Selecione um torneio";
      if (!form.inscricoes || isNaN(form.inscricoes) || Number(form.inscricoes) < 1)
        errors.inscricoes = "Mínimo 1";

      const t = getTournament(form.torneioId);
      if (!form.plataforma && !t?.plataforma) {
        errors.plataforma = "Selecione a plataforma";
      }
      if (t?.modalidade === "SAT") {
        const ticketVal = form.tickets === "" ? 0 : Number(form.tickets);
        if (isNaN(ticketVal) || ticketVal < 0)
          errors.tickets = "Valor inválido";
      } else {
        const premioVal = form.premiacao === "" ? 0 : Number(form.premiacao);
        if (isNaN(premioVal) || premioVal < 0)
          errors.premiacao = "Valor inválido";
      }
      return errors;
    },
    [getTournament]
  );

  const saveRegistro = useCallback(
    (form, editingId) => {
      const errors = validateRegistro(form);
      if (Object.keys(errors).length) return { success: false, errors };

      const t = getTournament(form.torneioId);
      const ticketVal = form.tickets === "" ? "0" : form.tickets;
      const premioVal = form.premiacao === "" ? "0" : form.premiacao;

      setRegistros((prev) =>
        editingId
          ? prev.map((r) =>
              r.id === editingId
                ? {
                    ...r,
                    torneioId: form.torneioId,
                    plataforma: form.plataforma || t?.plataforma || "",
                    inscricoes: form.inscricoes,
                    premiacao:
                      t?.modalidade === "SAT" ? "" : premioVal,
                    tickets: t?.modalidade === "SAT" ? ticketVal : "",
                    isSatellite: form.isSatellite,
                  }
                : r
            )
          : [
              {
                id: Date.now(),
                torneioId: form.torneioId,
                plataforma: form.plataforma || t?.plataforma || "",
                inscricoes: form.inscricoes,
                premiacao: t?.modalidade === "SAT" ? "" : premioVal,
                tickets: t?.modalidade === "SAT" ? ticketVal : "",
                isSatellite: form.isSatellite,
                registradoEm: new Date().toISOString(),
              },
              ...prev,
            ]
      );

      return { success: true, errors: {} };
    },
    [validateRegistro, getTournament, setRegistros]
  );

  const deleteRegistro = useCallback(
    (id) => {
      setRegistros((p) => p.filter((r) => r.id !== id));
    },
    [setRegistros]
  );

  return {
    getTournament,
    validateRegistro,
    saveRegistro,
    deleteRegistro,
  };
};
